import React, { useState, useEffect, useMemo } from 'react';
import {
  LayoutDashboard, 
  ShoppingCart, 
  TrendingUp, 
  Package, 
  PlusCircle, 
  Download,
  Trash2,
  Bird,
  Egg,
  Warehouse
} from 'lucide-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  AreaChart, 
  Area 
} from 'recharts';

const App = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [farmData, setFarmData] = useState(() => {
    const saved = localStorage.getItem('magaibia_farm_data');
    return saved ? JSON.parse(saved) : {
      transactions: [],
      inventory: { birdsCount: 0, eggsCount: 0, feedStockKg: 0 }
    };
  });
  
  const [isAdding, setIsAdding] = useState(false);
  const [newTx, setNewTx] = useState({
    type: 'PURCHASE',
    category: 'FEED',
    item: '',
    quantity: 0,
    unitPrice: 0,
    note: ''
  });
  
  useEffect(() => {
    localStorage.setItem('magaibia_farm_data', JSON.stringify(farmData));
  }, [farmData]);

  const stats = useMemo(() => {
    const revenue = farmData.transactions
      .filter(t => t.type === 'SALE')
      .reduce((sum, t) => sum + t.totalPrice, 0);
    const expenses = farmData.transactions
      .filter(t => t.type === 'PURCHASE')
      .reduce((sum, t) => sum + t.totalPrice, 0);
    return { revenue, expenses, profit: revenue - expenses };
  }, [farmData.transactions]);

  const chartData = useMemo(() => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return d.toISOString().split('T')[0];
    });

    return last7Days.map(date => {
      const dayTxs = farmData.transactions.filter(t => t.timestamp.startsWith(date));
      const income = dayTxs.filter(t => t.type === 'SALE').reduce((s, t) => s + t.totalPrice, 0);
      const outcome = dayTxs.filter(t => t.type === 'PURCHASE').reduce((s, t) => s + t.totalPrice, 0);
      return { date: date.slice(5), income, outcome };
    });
  }, [farmData.transactions]);

  const handleAddTransaction = (e) => {
    e.preventDefault();
    if (!newTx.item || !newTx.quantity || !newTx.unitPrice) return;

    const transaction = {
      id: crypto.randomUUID(),
      type: newTx.type,
      category: newTx.category,
      item: newTx.item,
      quantity: Number(newTx.quantity),
      unitPrice: Number(newTx.unitPrice),
      totalPrice: Number(newTx.quantity) * Number(newTx.unitPrice),
      timestamp: new Date().toISOString(),
      note: newTx.note
    };

    const updatedInventory = { ...farmData.inventory };
    if (transaction.type === 'PURCHASE') {
      if (transaction.category === 'CHICKS' || transaction.category === 'CHICKENS') updatedInventory.birdsCount += transaction.quantity;
      if (transaction.category === 'FEED') updatedInventory.feedStockKg += transaction.quantity;
    } else {
      if (transaction.category === 'CHICKENS') updatedInventory.birdsCount -= transaction.quantity;
      if (transaction.category === 'EGGS') updatedInventory.eggsCount -= transaction.quantity;
    }

    setFarmData(prev => ({
      transactions: [transaction, ...prev.transactions],
      inventory: updatedInventory
    }));

    setIsAdding(false);
    setNewTx({ type: 'PURCHASE', category: 'FEED', item: '', quantity: 0, unitPrice: 0, note: '' });
  };

  const deleteTransaction = (id) => {
    setFarmData(prev => ({
      ...prev,
      transactions: prev.transactions.filter(t => t.id !== id)
    }));
  };

  const updateInventoryCount = (field, val) => {
    setFarmData(prev => ({
      ...prev,
      inventory: { ...prev.inventory, [field]: Math.max(0, val) }
    }));
  };

  const exportToTxt = () => {
    const header = `MAGAIBIA POULTRY FARM - FULL RECORDS\nGenerated on: ${new Date().toLocaleString()}\n\n`;
    const summary = `SUMMARY:\nTotal Revenue: R${stats.revenue}\nTotal Expenses: R${stats.expenses}\nNet Profit: R${stats.profit}\n\n`;
    const inventory = `INVENTORY:\nBirds: ${farmData.inventory.birdsCount}\nEggs: ${farmData.inventory.eggsCount}\nFeed: ${farmData.inventory.feedStockKg}kg\n\n`;
    const logs = `TRANSACTION LOGS:\n` + farmData.transactions.map(t => 
      `[${t.timestamp}] ${t.type} - ${t.item} (${t.category}): Qty ${t.quantity}, Price R${t.unitPrice}, Total R${t.totalPrice} | Note: ${t.note || 'N/A'}`
    ).join('\n');

    const blob = new Blob([header + summary + inventory + logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MagaibiaFarm_Records_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      {/* Sidebar Navigation */}
      <nav style={{ width: '100%', maxWidth:  '18rem', background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '1rem', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', paddingLeft: '0.5rem' }}>
          <div style={{ background: '#f59e0b', padding: '0.5rem', borderRadius: '0.5rem' }}>
            <Bird style={{ color: '#fff' }} size={24} />
          </div>
          <h1 style={{ fontWeight: 700, fontSize: '1.125rem' }}>Magaibia <span style={{ color: '#f59e0b' }}>Farm</span></h1>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <NavItem active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} icon={<LayoutDashboard size={20} />} label="Dashboard" />
          <NavItem active={activeTab === 'transactions'} onClick={() => setActiveTab('transactions')} icon={<ShoppingCart size={20} />} label="Transactions" />
          <NavItem active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')} icon={<Package size={20} />} label="Inventory" />
        </div>

        <div style={{ marginTop: 'auto', paddingTop: '2rem' }}>
          <button onClick={exportToTxt} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', color: '#475569', borderRadius: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <Download size={16} /> Export Records (.txt)
          </button>
        </div>
      </nav>

      {/* Main Content Area */}
      <main style={{ flex: 1, padding: '1rem', overflowY: 'auto' }}>
        {/* Header Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, textTransform: 'capitalize' }}>{activeTab}</h2>
              <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Manage your poultry business efficiently.</p>
          </div>
          <button onClick={() => setIsAdding(true)} style={{ background: '#f59e0b', color: '#fff', padding: '0.5rem 1rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none', cursor: 'pointer' }}>
            <PlusCircle size={20} /> Add Record
          </button>
        </div>

        {activeTab === 'dashboard' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Quick Stats */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <StatCard label="Total Revenue" value={`R${stats.revenue.toLocaleString()}`} color="emerald" />
              <StatCard label="Total Expenses" value={`R${stats.expenses.toLocaleString()}`} color="rose" />
              <StatCard label="Net Profit" value={`R${stats.profit.toLocaleString()}`} color="amber" />
            </div>

            {/* Charts */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '1rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', flex: 1, minWidth: '18rem' }}>
                <h3 style={{ fontWeight: 600, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><TrendingUp size={18} style={{ color: '#f59e0b' }} /> Cashflow (Last 7 Days)</h3>
                <div style={{ height: '16rem' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} />
                      <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                      <Area type="monotone" dataKey="income" stroke="#10b981" fillOpacity={1} fill="url(#colorIncome)" />
                      <Area type="monotone" dataKey="outcome" stroke="#f43f5e" fillOpacity={0} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '1rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', flex: 1, minWidth: '18rem' }}>
                <h3 style={{ fontWeight: 600, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Package size={18} style={{ color: '#f59e0b' }} /> Recent Activity</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {farmData.transactions.slice(0, 5).map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: '#f8fafc', borderRadius: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ padding: '0.5rem', borderRadius: '9999px', background: t.type === 'SALE' ? '#ecfdf5' : '#fff1f2', color: t.type === 'SALE' ? '#065f46' : '#be123c' }}>
                          {t.type === 'SALE' ? <TrendingUp size={16} /> : <ShoppingCart size={16} />}
                        </div>
                        <div>
                          <p style={{ fontSize: '0.875rem', fontWeight: 600 }}>{t.item}</p>
                          <p style={{ fontSize: '0.75rem', color: '#64748b' }}>{new Date(t.timestamp).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <p style={{ fontSize: '0.875rem', fontWeight: 700, color: t.type === 'SALE' ? '#065f46' : '#be123c' }}>{t.type === 'SALE' ? '+' : '-'}R{t.totalPrice}</p>
                    </div>
                  ))}
                  {farmData.transactions.length === 0 && <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem 0' }}>No records yet.</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div style={{ backgroundColor: '#fff', borderRadius: '1rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', textAlign: 'left' }}>
                <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <tr>
                    <th style={{ padding: '0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Date</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Type</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Item</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Qty</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Total</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {farmData.transactions.map(t => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.75rem', color: '#475569' }}>{new Date(t.timestamp).toLocaleDateString()}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{ padding: '0.25rem 0.5rem', borderRadius: '9999px', backgroundColor: t.type === 'SALE' ? '#ecfdf5' : '#fff1f2', color: t.type === 'SALE' ? '#065f46' : '#be123c', fontSize: '0.75rem', fontWeight: 700 }}>{t.type}</span>
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <p style={{ fontWeight: 600, margin: 0 }}>{t.item}</p>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>{t.category}</p>
                      </td>
                      <td style={{ padding: '0.75rem', color: '#475569' }}>{t.quantity}</td>
                      <td style={{ padding: '0.75rem', fontWeight: 700 }}>R{t.totalPrice}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <button onClick={() => deleteTransaction(t.id)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {farmData.transactions.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No transactions recorded yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'inventory' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            <InventoryCard 
              icon={<Bird size={32} style={{ color: '#f59e0b' }} />} 
              label="Bird Population" 
              value={farmData.inventory.birdsCount} 
              onUpdate={(v) => updateInventoryCount('birdsCount', v)}
            />
            <InventoryCard 
              icon={<Egg size={32} style={{ color: '#f59e0b' }} />} 
              label="Egg Stock" 
              value={farmData.inventory.eggsCount} 
              onUpdate={(v) => updateInventoryCount('eggsCount', v)}
            />
            <InventoryCard 
              icon={<Warehouse size={32} style={{ color: '#f59e0b' }} />} 
              label="Feed Stock (kg)" 
              value={farmData.inventory.feedStockKg} 
              onUpdate={(v) => updateInventoryCount('feedStockKg', v)}
            />
          </div>
        )}
      </main>

      {/* Transaction Modal */}
      {isAdding && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 50 }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: '28rem', borderRadius: '1rem', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #f1f5f9' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 700 }}>New Transaction</h3>
              <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Record a buy or sell event.</p>
            </div>
            <form onSubmit={handleAddTransaction} style={{ padding: '1.5rem', display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Type</label>
                  <select 
                    value={newTx.type} 
                    onChange={e => setNewTx({...newTx, type: e.target.value})}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                  >
                    <option value='PURCHASE'>Purchase (Buy)</option>
                    <option value='SALE'>Sale (Sell)</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Category</label>
                  <select 
                    value={newTx.category} 
                    onChange={e => setNewTx({...newTx, category: e.target.value})}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                  >
                    {['CHICKS','CHICKENS','EGGS','FEED','MEDICINE','EQUIPMENT','OTHER'].map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Item Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Layers Chick Feed"
                  value={newTx.item}
                  onChange={e => setNewTx({...newTx, item: e.target.value})}
                  required 
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Quantity</label>
                  <input 
                    type="number" 
                    value={newTx.quantity || ''}
                    onChange={e => setNewTx({...newTx, quantity: Number(e.target.value)})}
                    required 
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Unit Price (R)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={newTx.unitPrice || ''}
                    onChange={e => setNewTx({...newTx, unitPrice: Number(e.target.value)})}
                    required 
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                  />
                </div>
              </div>

              <div style={{ paddingTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                <button type="button" onClick={() => setIsAdding(false)} style={{ flex: 1, padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none', background: '#f59e0b', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// UI Components
const NavItem = ({ active, onClick, icon, label }) => (
  <button 
    onClick={onClick}
    style={{ 
      width: '100%', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.75rem', 
      padding: '0.5rem 0.75rem', 
      borderRadius: '0.75rem', 
      fontSize: '0.875rem', 
      fontWeight: 600, 
      cursor: 'pointer', 
      backgroundColor: active ? '#fffbeb' : 'transparent', 
      color: active ? '#c2410c' : '#475569', 
      border: active ? '1px solid rgba(245,158,11,0.1)' : 'none', 
      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.03)' : 'none' 
    }}
  >
    <span style={{ color: active ? '#c2410c' : '#94a3b8' }}>{icon}</span>
    {label}
  </button>
);

const StatCard = ({ label, value, color, trend }) => {
  const colorMap = {
    emerald: { bg: '#ecfdf5', text: '#065f46' },
    rose: { bg: '#fff1f2', text: '#be123c' },
    amber: { bg: '#fffbeb', text: '#92400e' }
  };
  return (
    <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderRadius: '1rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', minWidth: '12rem', flex: '1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
        {trend && <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.25rem 0.5rem', borderRadius: '9999px', backgroundColor: colorMap[color].bg, color: colorMap[color].text }}>{trend}</span>}
      </div>
      <p style={{ fontSize: '1.75rem', fontWeight: 700 }}>{value}</p>
    </div>
  );
};

const InventoryCard = ({ icon, label, value, onUpdate }) => (
  <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderRadius: '1rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
    <div style={{ marginBottom: '1rem' }}>{icon}</div>
    <h3 style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{label}</h3>
    <p style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1.5rem' }}>{value}</p>
    <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
      <button onClick={() => onUpdate(value - 1)} style={{ flex: 1, backgroundColor: '#f1f5f9', border: 'none', padding: '0.5rem', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 700 }}>-</button>
      <button onClick={() => onUpdate(value + 1)} style={{ flex: 1, backgroundColor: '#fff7ed', border: 'none', padding: '0.5rem', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 700, color: '#b45309' }}>+</button>
    </div>
  </div>
);

export default App;
